<template>
  <div class="traceability-results">
    <header
      class="traceability-results-header"
      :style="{
        marginBottom: detailsLevel === TraceabilityDetailsLevel.NTO ? 0 : '1rem'
      }"
    >
      <div class="traceability-results-title">
        {{ title }}
      </div>

      <template v-if="detailsLevel === TraceabilityDetailsLevel.NTO && firstResult">
        <div class="traceability-results-tags">
          <div
            v-for="examType in firstResult.examsTypes"
            class="traceability-results-tag"
          >
            {{ examType }}
          </div>
        </div>

        <div class="traceability-results-location">
          <div class="traceability-results-location-icon">
            <svg-loader
              src="/static/images/icon-business.svg"
            />
          </div>

          <div class="traceability-results-location-content">
            <div class="traceability-results-location-content-label">
              Localização
            </div>

            <div class="traceability-results-location-content-value">
              {{ firstResult.address }}
            </div>
          </div>
        </div>

        <div class="traceability-results-total">
          <div class="traceability-results-total-content">
            <div class="traceability-results-total-content-label">
              {{ totalByItemLabel }}
            </div>

            <div class="traceability-results-total-content-updated-at">
              Atualizado <b>{{ updatedAtFormatted }}</b>
            </div>
          </div>

          <div class="traceability-results-total-value">
            {{ firstResult.total }}
          </div>
        </div>

        <img
          :src="ntoImageSource"
          alt="NTO"
          class="traceability-results-image"
        />
      </template>

      <div
        v-if="detailsLevel !== TraceabilityDetailsLevel.NTO"
        class="traceability-results-updated-at"
      >
        Atualizado <b>{{ updatedAtFormatted }}</b>
      </div>
    </header>

    <div
      v-if="detailsLevel !== TraceabilityDetailsLevel.NTO"
      class="traceability-results-items"
      :style="{
        gap: detailsLevel === TraceabilityDetailsLevel.Country ? '0.5rem' : '1rem'
      }"
    >
      <template v-if="results.length">
        <traceability-results-item
          v-for="result in results"
          :key="result.id"
          :result="result"
          :details-level="detailsLevel"
          :type="type"
          @click-result="handleClickResult"
          @click-details="handleClickDetails"
        />
      </template>

      <div v-else>Nenhuma NTO encontrada.</div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

import { TraceabilityDetailsLevel } from '@/enums'

import {
  brazilianFederativeUnits,
  brazilianFederativeUnitsAcronyms,
  ONE_SECOND_IN_MILLISECONDS
} from '@/constants'

import TraceabilityResultsItem from './TraceabilityResultsItem'

export default {
  name: 'TraceabilityResults',
  components: {
    TraceabilityResultsItem
  },
  props: {
    results: {
      type: Array,
      required: true
    },
    detailsLevel: {
      type: Number,
      default: TraceabilityDetailsLevel.Country
    },
    updatedAt: {
      type: Date,
      default: undefined
    },
    type: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      TraceabilityDetailsLevel,
      updatedAtFormatted: undefined,
      updatedAtAutoFormatterInterval: undefined
    }
  },
  computed: {
    firstResult() {
      const [firstResult] = this.results

      return firstResult
    },
    title() {
      switch (this.detailsLevel) {
        case TraceabilityDetailsLevel.Country:
          return this.$route.name === 'productionTraceability' ? 'NTOs com fluxo de produção' : 'NTOs com fluxo logístico'

        case TraceabilityDetailsLevel.State:
          const acronymIndex = brazilianFederativeUnitsAcronyms.findIndex((acronym) => acronym === this.firstResult?.state)
          const stateName = brazilianFederativeUnits[acronymIndex]

          return stateName ? `NTOs ${stateName}` : 'NTOs'

        default:
          return this.firstResult?.destination
      }
    },
    totalByItemLabel() {
      return this.$route.name === 'productionTraceability' ? 'Total de exames' : 'Total de amostras'
    },
    ntoImageSource() {
      if (this.firstResult?.imageSource && this.firstResult?.imageSource !== 'MOCKED') {
        return this.firstResult?.imageSource
      }

      return '/static/images/nto.jpg'
    }
  },
  watch: {
    updatedAt: {
      handler() {
        this.updateFormattedUpdatedAt()
      },
      immediate: true
    }
  },
  mounted() {
    this.createUpdatedAtAutoFormatter()
  },
  beforeDestroy() {
    this.destroyUpdatedAtAutoFormatter()
  },
  methods: {
    handleClickResult(result) {
      this.$emit('click-result', result)
    },
    handleClickDetails(result) {
      this.$emit('click-details', result)
    },
    updateFormattedUpdatedAt() {
      this.updatedAtFormatted = moment(this.updatedAt).fromNow()
    },
    createUpdatedAtAutoFormatter() {
      this.updateFormattedUpdatedAt()

      this.updatedAtAutoFormatterInterval = setInterval(() => {
        this.updateFormattedUpdatedAt()
      }, ONE_SECOND_IN_MILLISECONDS)
    },
    destroyUpdatedAtAutoFormatter() {
      clearInterval(this.updatedAtAutoFormatterInterval)
    }
  }
}
</script>

<style lang="scss" scoped>
.traceability-results {
  font-family: 'Exo 2';
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &-header {
    padding: 1.5rem;
    padding-bottom: 0;
  }

  &-items {
    max-height: 600px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    padding-left: 1.5rem;
    padding-right: 1rem;
    margin-right: 0.5rem;
  }

  &-tags {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-top: 0.5rem;
    margin-bottom: 1rem;
  }

  &-tag {
    background: linear-gradient(90deg, #DB310F 0%, #CE125D 100%);
    border-radius: 0.25rem;
    gap: 0.5rem;
    padding: 0.25rem 0.5rem;
    text-transform: uppercase;
    font-weight: 600;
    font-size: 0.75rem;
    color: #FFFFFF;
  }

  &-location {
    color: #7F1A54;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    margin-bottom: 1rem;

    &-icon {
      .md-icon {
        width: 1.25rem;
        height: 1.25rem;
        min-width: auto;
        min-height: auto;
      }
    }

    &-content {
      font-size: 0.875rem;
      min-width: 0;
      flex: 1; 

      &-label {
        font-weight: 700;
      }

      &-value {
        font-weight: 400;
        word-wrap: break-word; /* Quebra palavras longas */
        overflow-wrap: break-word; /* Método mais moderno */
        word-break: break-word; /* Quebra palavras quando necessário */
      }
    }
  }

  &-total {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;

    &-content {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      &-label {
        font-weight: 600;
        font-size: 0.875rem;
        text-transform: uppercase;
        color: #818181;
      }

      &-updated-at {
        font-weight: 400;
        font-size: 0.75rem;
        color: #999999;
      }
    }

    &-value {
      font-weight: 700;
      font-size: 1.5rem;
      color: #7F1A54;
    }
  }

  &-title {
    font-weight: 700;
    font-size: 1.125rem;
    color: #EC2072;
  }

  &-updated-at {
    font-weight: 400;
    font-size: 0.75rem;
    color: #999999;
  }

  &-image {
    width: 100%;
    object-fit: cover;
    border-radius: 0.375rem;
    margin-bottom: 1.5rem;
  }
}
</style>
